import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, X, FileBox, Loader2 } from "lucide-react";
import { getToken } from "../lib/authApi";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = ["step", "stp", "iges", "igs", "stl", "dxf", "dwg", "pdf", "zip", "png", "jpg", "jpeg", "webp"];
const MAX_SIZE = 50 * 1024 * 1024;

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const FileUpload = ({ files, onChange, testId = "file-upload", maxFiles = 50 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    let current = [...files];
    e.target.value = "";
    for (const file of selected) {
      if (current.length >= maxFiles) { toast.error(`Maximum ${maxFiles} files.`); break; }
      const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
      if (!ALLOWED.includes(ext)) {
        toast.error(`${file.name}: unsupported CAD/document/image type`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: exceeds 50MB limit`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const token = getToken();
        const { data } = await axios.post(`${API}/api/uploads`, fd, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        current = [...current, data];
        onChange(current);
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
  };

  const remove = (id) => onChange(files.filter((f) => f.id !== id));

  return (
    <div className="space-y-3" data-testid={testId}>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={handleFiles} data-testid={`${testId}-input`} accept=".step,.stp,.iges,.igs,.stl,.dxf,.dwg,.pdf,.zip,.png,.jpg,.jpeg,.webp" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`${testId}-btn`}
        className="w-full flex items-center justify-center gap-3 px-4 py-5 rounded-lg border border-dashed border-border bg-muted/50 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-200"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Paperclip className="h-4 w-4" strokeWidth={1.5} />}
        {uploading ? "Uploading..." : "Attach CAD / PDF / drawing images · max 50MB"}
      </button>
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-muted border border-border text-sm" data-testid={`${testId}-item-${f.id}`}>
              <FileBox className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} />
              <span className="truncate flex-1">{f.original_filename}</span>
              <span className="hidden sm:inline text-[10px] uppercase font-mono-tech px-1.5 py-0.5 rounded bg-background border border-border text-muted-foreground" data-testid={`${testId}-type-${f.id}`}>{(f.original_filename.split(".").pop() || "file").toLowerCase()}{/\.(dxf|pdf|png|jpe?g|webp)$/i.test(f.original_filename) ? " · preview" : " · metadata only"}</span>
              <span className="text-xs text-muted-foreground font-mono-tech">{fmtSize(f.size)}</span>
              <span className="text-[10px] font-mono-tech text-emerald-600" data-testid={`${testId}-status-${f.id}`}>Uploaded</span>
              <button type="button" onClick={() => remove(f.id)} aria-label="Remove file" data-testid={testId === "file-upload" ? `file-remove-${f.id}` : `${testId}-remove-${f.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
